/**
 * EnrichmentPanel Component
 *
 * Container for AI-powered suggestions during task creation.
 * Handles both Tier 1 (immediate) and Tier 2 (background) enrichment.
 *
 * Features:
 * - Debounced enrichment trigger (300ms after typing stops)
 * - Tier 1: Labels, priority, enhanced title (~100-200ms)
 * - Tier 2: Code hints, subtask proposals (1-2s, polls every 1s)
 * - Graceful degradation when AI is unavailable
 */

import {
  Component,
  Show,
  createSignal,
  createEffect,
  onCleanup,
  untrack,
} from "solid-js";
import { SuggestedLabels } from "./SuggestedLabels";
import { Tier2Details } from "./Tier2Details";
import { DegradedOutputBadge } from "../quota/DegradedOutputBadge";
import { enrichDraft, pollTier2Enrichment } from "../../api/enrichment";
import type {
  ProjectId,
  LabelSuggestion,
  Priority,
  QuotaScope,
  SubtaskProposal,
  Tier1Enrichment,
  Tier2Enrichment,
  EnrichmentStatus,
} from "../../api";

export interface EnrichmentPanelProps {
  projectId: ProjectId;
  title: string;
  description?: string;
  /** Debounce delay in ms before triggering enrichment */
  debounceMs?: number;
  /** Callback when user selects labels */
  onLabelsSelected?: (labels: LabelSuggestion[]) => void;
  /** Callback when user selects priority */
  onPrioritySelected?: (priority: Priority) => void;
  /** Callback when user wants to apply enhanced title */
  onEnhancedTitle?: (title: string) => void;
  /** Callback when user wants to apply suggested description */
  onSuggestedDescription?: (description: string) => void;
  /** Callback when subtask selection changes */
  onSubtasksSelectionChange?: (subtasks: SubtaskProposal[]) => void;
  /** Callback when AI classifies work item type (called with null when loading starts, type when complete) */
  onTypeClassified?: (suggestedType: string | null, isLoading: boolean) => void;
}

const EnrichmentSkeleton: Component = () => {
  return (
    <div class="animate-pulse space-y-2.5 min-h-[60px]">
      {/* Label suggestions skeleton */}
      <div class="flex gap-1.5">
        <div class="h-5 w-14 bg-line rounded-full" />
        <div class="h-5 w-18 bg-line rounded-full" />
        <div class="h-5 w-12 bg-line rounded-full" />
      </div>
      {/* Priority suggestion skeleton */}
      <div class="flex items-center gap-2">
        <div class="h-4 w-14 bg-line rounded" />
        <div class="h-5 w-16 bg-line rounded" />
      </div>
    </div>
  );
};

interface PrioritySuggestionProps {
  suggested: Priority;
  selected: Priority | null;
  onSelect: (priority: Priority) => void;
}

const PrioritySuggestion: Component<PrioritySuggestionProps> = (props) => {
  const priorityColors: Record<Priority, string> = {
    critical: "bg-signal-red-tint text-signal-red hover:bg-line hover:text-ink",
    high: "bg-signal-amber-tint text-signal-amber hover:bg-line hover:text-ink",
    medium: "bg-surface text-ink-2 hover:bg-line hover:text-ink",
    low: "bg-surface text-ink-2 hover:bg-line hover:text-ink",
    none: "bg-surface text-ink-3 hover:bg-line hover:text-ink",
  };

  const isSelected = () => props.selected === props.suggested;

  return (
    <div class="flex items-center gap-2">
      <span class="text-xs text-ink-3">Priority:</span>
      <button
        type="button"
        class={`
          px-2 py-0.5 rounded text-xs font-medium transition-colors focus-ring
          ${isSelected() ? "bg-ink text-paper" : priorityColors[props.suggested]}
        `}
        onClick={() => props.onSelect(props.suggested)}
      >
        {props.suggested}
      </button>
    </div>
  );
};

interface AppliedSuggestionProps {
  /** What was applied, e.g. "Title" — rendered as "<label> applied:" */
  label: string;
  /** The applied value, already truncated for display */
  value: string;
  /** Omitted when the pre-apply value is unknown and undo is not offered */
  onUndo?: () => void;
}

const AppliedSuggestion: Component<AppliedSuggestionProps> = (props) => {
  return (
    <div class="flex items-center gap-1.5 text-signal-green bg-signal-green-tint rounded px-2 py-1">
      <svg
        class="w-4 h-4 shrink-0"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M5 13l4 4L19 7"
        />
      </svg>
      <span class="text-signal-green">{props.label} applied:</span>
      <span class="text-signal-green truncate">{props.value}</span>
      <Show when={props.onUndo}>
        <button
          type="button"
          aria-label={`Undo applied ${props.label.toLowerCase()}`}
          class="ml-auto shrink-0 flex items-center gap-1 px-1.5 py-0.5 rounded text-signal-green hover:text-ink underline focus-ring"
          onClick={() => props.onUndo?.()}
        >
          <svg
            class="w-3.5 h-3.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M3 10h10a5 5 0 015 5v2M3 10l4-4M3 10l4 4"
            />
          </svg>
          Undo
        </button>
      </Show>
    </div>
  );
};

export const EnrichmentPanel: Component<EnrichmentPanelProps> = (props) => {
  const [loading, setLoading] = createSignal(false);
  const [tier1, setTier1] = createSignal<Tier1Enrichment | null>(null);
  const [tier2, setTier2] = createSignal<Tier2Enrichment | null>(null);
  const [tier2Status, setTier2Status] =
    createSignal<EnrichmentStatus>("not_requested");
  const [error, setError] = createSignal<string | null>(null);
  // Suggestions came from the quota fallback tier (FLOW-794), and which
  // ceiling bound them to it, when the backend named one (FLOW-930)
  const [degraded, setDegraded] = createSignal(false);
  const [degradedScope, setDegradedScope] = createSignal<
    QuotaScope | undefined
  >();
  const [enrichmentId, setEnrichmentId] = createSignal<string | null>(null);

  const [selectedLabels, setSelectedLabels] = createSignal<Set<string>>(
    new Set(),
  );
  const [selectedPriority, setSelectedPriority] = createSignal<Priority | null>(
    null,
  );
  const [appliedDescription, setAppliedDescription] = createSignal<
    string | null
  >(null);

  // Pre-apply values, kept so an applied suggestion can be reverted
  const [previousTitle, setPreviousTitle] = createSignal<string | null>(null);
  const [previousDescription, setPreviousDescription] = createSignal<
    string | null
  >(null);

  // Values an undo just restored — the enrichment they would re-trigger is skipped
  let undoRestored: { title: string; description: string } | null = null;

  let debounceTimer: ReturnType<typeof setTimeout> | undefined;
  let pollTimer: ReturnType<typeof setInterval> | undefined;
  let pollCount = 0;
  let hasTimedOut = false; // Track timeout to prevent late API responses from overwriting
  const MAX_POLL_COUNT = 30; // 30 seconds max polling (Tier 2 involves AI + GitHub validation)

  // Debounced enrichment trigger
  createEffect(() => {
    const title = props.title;
    const description = props.description;

    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    // Skip the enrichment an undo would re-trigger — the suggestion it reverted
    // is still on screen and should stay offered rather than be re-fetched
    if (
      undoRestored &&
      undoRestored.title === title &&
      undoRestored.description === (description ?? "")
    ) {
      undoRestored = null;
      return;
    }

    // Don't enrich if title is too short
    if (title.length < 3) {
      setTier1(null);
      setTier2(null);
      setError(null);
      return;
    }

    // Skip enrichment if user just accepted the suggested title
    // (prevents re-triggering when clicking "Suggested title" button)
    // Use untrack() to read tier1 without creating a reactive dependency
    const currentTier1 = untrack(() => tier1());
    if (currentTier1?.enhanced_title === title) {
      return;
    }

    // Skip enrichment if user just accepted the suggested description
    // (prevents re-triggering when clicking "Suggested description" button)
    // Only skip if there's actually a suggested description and it matches
    if (
      currentTier1?.suggested_description &&
      currentTier1.suggested_description === description
    ) {
      return;
    }

    // Debounce the API call
    debounceTimer = setTimeout(async () => {
      await triggerEnrichment(title, description);
    }, props.debounceMs ?? 300);
  });

  // Poll for Tier 2 results
  createEffect(() => {
    const id = enrichmentId();
    const status = tier2Status();

    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = undefined;
    }

    if (id && status === "processing") {
      pollCount = 0;
      hasTimedOut = false;
      pollTimer = setInterval(async () => {
        pollCount++;
        if (pollCount > MAX_POLL_COUNT) {
          clearInterval(pollTimer);
          pollTimer = undefined;
          hasTimedOut = true;
          setTier2Status("timed_out");
          return;
        }

        try {
          const result = await pollTier2Enrichment(id);
          // Don't let late API responses overwrite timeout status
          if (hasTimedOut) {
            return;
          }
          setTier2Status(result.status);
          if (result.tier2) {
            setTier2(result.tier2);
          }
          if (result.status !== "processing") {
            clearInterval(pollTimer);
            pollTimer = undefined;
          }
        } catch (e) {
          console.error("Tier 2 poll failed:", e);
        }
      }, 1000);
    }
  });

  onCleanup(() => {
    if (debounceTimer) clearTimeout(debounceTimer);
    if (pollTimer) clearInterval(pollTimer);
  });

  const triggerEnrichment = async (title: string, description?: string) => {
    setLoading(true);
    setError(null);
    setDegraded(false);
    setDegradedScope(undefined);
    // Reset applied/undo state for new enrichment
    setAppliedDescription(null);
    setPreviousTitle(null);
    setPreviousDescription(null);
    props.onTypeClassified?.(null, true);

    try {
      const response = await enrichDraft(props.projectId, {
        title,
        description,
        include_tier2: true,
      });

      if (response.error) {
        setError(response.error);
      }

      if (response.tier1) {
        setTier1(response.tier1);
        // Notify with the classified type (or null if not present)
        props.onTypeClassified?.(response.tier1.suggested_type ?? null, false);
      } else {
        props.onTypeClassified?.(null, false);
      }

      setEnrichmentId(response.enrichment_id);
      setTier2Status(response.tier2_status);
      setDegraded(response.degraded);
      setDegradedScope(response.degradedScope);

      if (response.tier2) {
        setTier2(response.tier2);
      }
    } catch {
      setError("AI suggestions temporarily unavailable");
      props.onTypeClassified?.(null, false);
    } finally {
      setLoading(false);
    }
  };

  const handleLabelToggle = (label: LabelSuggestion) => {
    const current = new Set(selectedLabels());
    if (current.has(label.name)) {
      current.delete(label.name);
    } else {
      current.add(label.name);
    }
    setSelectedLabels(current);

    const selected =
      tier1()?.suggested_labels.filter((l) => current.has(l.name)) ?? [];
    props.onLabelsSelected?.(selected);
  };

  const handlePrioritySelect = (priority: Priority) => {
    setSelectedPriority(priority);
    props.onPrioritySelected?.(priority);
  };

  const handleApplyTitle = (suggested: string) => {
    setPreviousTitle(props.title);
    props.onEnhancedTitle?.(suggested);
  };

  const handleUndoTitle = () => {
    const previous = previousTitle();
    if (previous === null) return;
    undoRestored = { title: previous, description: props.description ?? "" };
    setPreviousTitle(null);
    props.onEnhancedTitle?.(previous);
  };

  const handleApplyDescription = (suggested: string) => {
    setPreviousDescription(props.description ?? "");
    setAppliedDescription(suggested);
    props.onSuggestedDescription?.(suggested);
  };

  const handleUndoDescription = () => {
    const previous = previousDescription();
    if (previous === null) return;
    undoRestored = { title: props.title, description: previous };
    setPreviousDescription(null);
    setAppliedDescription(null);
    props.onSuggestedDescription?.(previous);
  };

  return (
    <div class="space-y-3 p-3 bg-sunken rounded border border-line min-h-[76px]">
      {/* Header */}
      <div class="flex items-center gap-2 text-sm font-medium text-ink-2">
        <svg
          class="w-4 h-4 text-signal-magenta"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
          />
        </svg>
        AI Suggestions
        <Show when={degraded()}>
          <DegradedOutputBadge
            onRegenerate={() =>
              void triggerEnrichment(props.title, props.description)
            }
            regenerating={loading()}
            scope={degradedScope()}
          />
        </Show>
      </div>

      {/* Error State */}
      <Show when={error()}>
        <div class="text-sm text-signal-amber bg-signal-amber-tint rounded p-2">
          {error()}
        </div>
      </Show>

      {/* Content */}
      <Show when={loading()} fallback={<Tier1Content />}>
        <EnrichmentSkeleton />
      </Show>
    </div>
  );

  // Helper component for Tier 1 content to avoid deep nesting.
  // `tier1()` is read by `Show`, never in this body: a component body runs
  // untracked, so a capture here bound the suggestions to whatever had arrived
  // the last time `loading()` flipped, and a result landing after that never
  // painted at all (FLOW-965).
  function Tier1Content() {
    return (
      <Show when={tier1()}>
        {(t1) => (
          <div class="space-y-3">
            {/* Enhanced Title */}
            <Show when={t1().enhanced_title}>
              {(enhancedTitle) => {
                // Applied while the title still matches the suggestion the user
                // accepted — a later edit re-enriches and clears the undo state
                const isApplied = () =>
                  previousTitle() !== null && props.title === enhancedTitle();

                return (
                  <Show
                    when={isApplied()}
                    fallback={
                      <Show when={enhancedTitle() !== props.title}>
                        <div class="text-sm">
                          <span class="text-ink-3">Suggested title: </span>
                          <button
                            type="button"
                            class="text-ink-2 hover:text-ink font-medium underline underline-offset-2 decoration-line-strong hover:decoration-ink"
                            onClick={() => handleApplyTitle(enhancedTitle())}
                          >
                            {enhancedTitle()}
                          </button>
                        </div>
                      </Show>
                    }
                  >
                    <div class="text-sm">
                      <AppliedSuggestion
                        label="Title"
                        value={enhancedTitle()}
                        onUndo={handleUndoTitle}
                      />
                    </div>
                  </Show>
                );
              }}
            </Show>

            {/* Suggested Description */}
            <Show when={t1().suggested_description}>
              {(suggestedDesc) => {
                // Show applied state if either:
                // 1. We explicitly tracked the apply action (appliedDescription signal)
                // 2. The current description matches the suggestion (handles race condition)
                const isApplied = () =>
                  appliedDescription() === suggestedDesc() ||
                  props.description === suggestedDesc();
                const truncated = () =>
                  suggestedDesc().length > 100
                    ? suggestedDesc().slice(0, 100) + "..."
                    : suggestedDesc();

                return (
                  <div class="text-sm">
                    <Show
                      when={isApplied()}
                      fallback={
                        <>
                          <span class="text-ink-3">
                            Suggested description:{" "}
                          </span>
                          <button
                            type="button"
                            class="text-ink-2 hover:text-ink font-medium underline underline-offset-2 decoration-line-strong hover:decoration-ink text-left"
                            onClick={() =>
                              handleApplyDescription(suggestedDesc())
                            }
                          >
                            {truncated()}
                          </button>
                        </>
                      }
                    >
                      <AppliedSuggestion
                        label="Description"
                        value={truncated()}
                        onUndo={
                          previousDescription() === null
                            ? undefined
                            : handleUndoDescription
                        }
                      />
                    </Show>
                  </div>
                );
              }}
            </Show>

            {/* Suggested Labels */}
            <Show when={t1().suggested_labels.length > 0}>
              <SuggestedLabels
                labels={t1().suggested_labels}
                selected={selectedLabels()}
                onToggle={handleLabelToggle}
              />
            </Show>

            {/* Suggested Priority */}
            <Show when={t1().suggested_priority}>
              <PrioritySuggestion
                suggested={t1().suggested_priority as Priority}
                selected={selectedPriority()}
                onSelect={handlePrioritySelect}
              />
            </Show>

            {/* Tier 2 Loading Indicator */}
            <Show when={tier2Status() === "processing"}>
              <div class="text-xs text-ink-3 flex items-center gap-1.5">
                <div class="animate-spin w-3 h-3 border border-line-strong border-t-ink rounded-full" />
                Analyzing code context...
              </div>
            </Show>

            {/* Tier 2 Timeout/Failure/Empty Notice */}
            <Show
              when={(() => {
                const status = tier2Status();
                const data = tier2();
                // Show "unavailable" when:
                // 1. Tier 2 timed out or failed without data, OR
                // 2. Tier 2 completed but produced no useful results (empty code_hints AND empty subtask_proposals)
                if ((status === "timed_out" || status === "failed") && !data) {
                  return true;
                }
                if (
                  status === "completed" &&
                  data &&
                  data.code_hints.length === 0 &&
                  data.subtask_proposals.length === 0
                ) {
                  return true;
                }
                return false;
              })()}
            >
              <div class="text-xs text-ink-3 italic">
                Code analysis unavailable
              </div>
            </Show>

            {/* Tier 2 Content */}
            <Show when={tier2()}>
              {(t2) => (
                <Tier2Details
                  codeHints={t2().code_hints}
                  subtaskProposals={t2().subtask_proposals}
                  onSubtasksSelectionChange={props.onSubtasksSelectionChange}
                />
              )}
            </Show>
          </div>
        )}
      </Show>
    );
  }
};
